import React from 'react';
import './subscription.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import PageHeader from '../../../components/base/PageHeader/PageHeader';
import {
  Button, Col, Container, Icons, Row,
} from '../../../components/base/BootstrapComponents';
import { getSubscriptionData } from '../reducer';
import notificationDecorator from '../../Notifications/notificationDecorator';
import { responseLogWithNotification } from '../../../utils/utils';
import {
  createInterval,
  createSubscription,
  deleteSubscription,
  editSubscription,
} from '../actions';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import SubscriptionDTO from '../../../dto/SubscriptionDTO';
import Switches from '../../../components/fields/Switches/Switches';
import TextInputWithLabel from '../../../components/fields/TextInput/TextInputWithLabel';
import Loader from '../../../components/base/Loader/Loader';
import IntervalGrid from './IntervalGrid';
import { showGlobalConfirm } from '../../System/actions';
import IntervalDTO from '../../../dto/IntervalDTO';
import DateTimePicker from '../../../components/fields/DatePicker/DateTimePicker';

const buildIntervalPayload = ({ startAt, endAt, paymentId }) => {
  const intervalStartAt = String(startAt || '').trim();
  const intervalEndAt = String(endAt || '').trim();
  const intervalPaymentId = String(paymentId || '').trim();
  const hasIntervalData = Boolean(intervalStartAt || intervalEndAt || intervalPaymentId);

  if (!hasIntervalData) {
    return null;
  }

  if (!intervalStartAt || !intervalEndAt) {
    notificationDecorator({
      title: 'Создание интервала',
      message: 'Укажите начало и конец периода',
    });
    return false;
  }

  const startMs = Date.parse(intervalStartAt);
  const endMs = Date.parse(intervalEndAt);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    notificationDecorator({
      title: 'Создание интервала',
      message: 'Конец периода должен быть позже начала',
    });
    return false;
  }

  const payload = {
    start_at: intervalStartAt,
    end_at: intervalEndAt,
  };

  if (intervalPaymentId) {
    payload.payment_id = intervalPaymentId;
  }

  return payload;
};

export default function SubscriptionForm(props) {
  const {
    handleClose = () => {}, handleEdit = () => {}, loadData = () => {}, hasView = false,
  } = props;
  const dispatch = useDispatch();
  const {
    currentSubscription,
    loadingIntervalList,
  } = useSelector(getSubscriptionData);
  const defaultValues = currentSubscription?.id
    ? {
      ...currentSubscription,
      is_vip: Boolean(currentSubscription.is_vip),
    }
    : {
      lpns: [''],
      card_uid: '',
      comment: '',
      is_lpn_exact: true,
      is_active: true,
      is_vip: false,
      interval_start_at: '',
      interval_end_at: '',
      interval_payment_id: '',
    };
  const {
    control, getValues, watch, setValue,
  } = useForm({
    defaultValues,
  });

  const fields = watch('lpns');
  const append = () => {
    setValue('lpns', [...fields, '']);
  };

  const remove = index => {
    const newItems = [...fields];
    newItems.splice(index, 1);
    setValue('lpns', newItems.length ? newItems : ['']);
  };

  const handleSave = () => {
    const isUpdate = currentSubscription?.id;
    const action = isUpdate ? editSubscription : createSubscription;
    const data = getValues();
    const {
      interval_start_at: intervalStartAt,
      interval_end_at: intervalEndAt,
      interval_payment_id: intervalPaymentId,
      ...subscriptionData
    } = data;
    const intervalPayload = !isUpdate ? buildIntervalPayload({
      startAt: intervalStartAt,
      endAt: intervalEndAt,
      paymentId: intervalPaymentId,
    }) : null;

    if (intervalPayload === false) {
      return;
    }

    const lpns = Array.isArray(subscriptionData.lpns)
      ? subscriptionData.lpns.map(item => String(item || '').trim()).filter(Boolean)
      : [];
    const cardUid = String(subscriptionData.card_uid || '').trim();
    const comment = String(subscriptionData.comment || '').trim();
    const payload = {
      ...subscriptionData,
      is_lpn_exact: Boolean(subscriptionData.is_lpn_exact),
      is_active: Boolean(subscriptionData.is_active),
      is_vip: Boolean(subscriptionData.is_vip),
    };

    if (lpns.length) {
      payload.lpns = lpns;
    } else {
      delete payload.lpns;
    }

    if (cardUid) {
      payload.card_uid = cardUid;
    } else {
      delete payload.card_uid;
    }

    if (comment) {
      payload.comment = comment;
    } else {
      delete payload.comment;
    }

    dispatch(action(payload))
      .then(createdSubscription => {
        if (!intervalPayload) {
          return null;
        }

        const subscriptionId = createdSubscription?.id || createdSubscription?.data?.id;
        if (!subscriptionId) {
          throw new Error('Не удалось определить созданный абонемент для добавления интервала');
        }

        return dispatch(createInterval({ id: subscriptionId, data: intervalPayload }));
      })
      .then(() => {
        loadData();
        handleClose();
        notificationDecorator({
          title: isUpdate ? 'Редактирование абонемента' : 'Создание абонемента',
          message: isUpdate
            ? 'Редактирование абонемента прошло успешно'
            : intervalPayload
              ? 'Создание абонемента и интервала прошло успешно'
              : 'Создание абонемента прошло успешно',
        });
      })
      .catch(responseLogWithNotification);
  };

  const handleDelete = () => {
    if (!currentSubscription?.id) {
      return;
    }

    dispatch(showGlobalConfirm({
      confirmTitle: 'Удаление абонемента',
      confirmMessage: `Вы уверены, что хотите удалить абонемент ${currentSubscription.id}?`,
      confirmButtonText: 'Удалить',
    })).then(() => {
      dispatch(deleteSubscription({ id: currentSubscription.id }))
        .then(() => {
          loadData();
          handleClose();
          notificationDecorator({
            title: 'Удаление абонемента',
            message: 'Успешно',
          });
        })
        .catch(responseLogWithNotification);
    }).catch(() => {});
  };

  const renderViewActions = () => {
    if (!hasView || !currentSubscription?.id) {
      return null;
    }

    return (
      <>
        <Button size="sm" className="px-3" onClick={handleEdit(currentSubscription)}>Редактировать</Button>
        <Button size="sm" variant="danger" className="px-3" onClick={handleDelete}>Удалить</Button>
        <Button size="sm" variant="secondary" className="px-3" onClick={handleClose}>Закрыть</Button>
      </>
    );
  };

  const subscriptionDTO = new SubscriptionDTO();
  const intervalDTO = new IntervalDTO();
  const attributes = subscriptionDTO.getAttributesByName();
  const intervalAttributes = intervalDTO.getAttributesByName();
  const intervalStartAtField = {
    ...intervalAttributes.start_at,
    name: 'interval_start_at',
  };
  const intervalEndAtField = {
    ...intervalAttributes.end_at,
    name: 'interval_end_at',
  };
  const intervalPaymentIdField = {
    ...intervalAttributes.payment_id,
    name: 'interval_payment_id',
  };

  const renderCreateIntervalForm = () => (
    <div className="subs-create-interval">
      <div className="subs-section-header">
        <div className="h4 mb-0">
          Первый интервал
        </div>
      </div>
      <FieldDTO
        control={control}
        type="text"
        className="mb-2"
        dtoObject={intervalStartAtField}
        addPlaceholder
        component={DateTimePicker}
        withLabel
        onChangeToIso
      />
      <FieldDTO
        control={control}
        type="text"
        className="mb-2"
        dtoObject={intervalEndAtField}
        addPlaceholder
        component={DateTimePicker}
        withLabel
        onChangeToIso
      />
      <FieldDTO
        control={control}
        type="text"
        className="mb-2"
        dtoObject={intervalPaymentIdField}
        addPlaceholder
        component={TextInputWithLabel}
      />
    </div>
  );

  return (
    <div className="page-container subs-form">
      <div className="inputs-box">
        <PageHeader header={hasView ? 'Абонемент' : currentSubscription?.id ? 'Редактирование абонемента' : 'Создание абонемента'} />
      </div>
      <div className="plp-table g-0">
        <div className="frame py-3 d-flex">
          <Container className="subs-form-container">
            <Row className="h-100">
              <Col columns={6}>
                <div className="subs-section-header">
                  <div className="h4 mb-0">
                    Данные абонемента
                  </div>
                  <div className="subs-section-actions">
                    {renderViewActions()}
                  </div>
                </div>
                {fields.map((item, index) => (
                  <div className="lpn-row" key={index}>
                    <FieldDTO
                      control={control}
                      type="text"
                      className="mb-2"
                      dtoObject={{
                        ...attributes.lpns,
                        name: `lpns.${index}`,
                        labelKey: 'subs.lpn',
                      }}
                      withLabel
                      addPlaceholder
                      component={TextInputWithLabel}
                      disabled={hasView}
                    />
                    {!hasView
                      ? (
                        <Button className="ms-3" onClick={() => remove(index)}>
                          <Icons width={24} height={24} customIcon icon="trash" />
                        </Button>
                      )
                      : null}

                  </div>
                ))}
                {!hasView
                  ? (
                    <Button className="btn-sm mb-2 mt-2" onClick={() => append('')}>Добавить ГРЗ</Button>
                  )
                  : null}

                <FieldDTO
                  control={control}
                  type="text"
                  className="mb-2"
                  dtoObject={attributes.card_uid}
                  addPlaceholder
                  component={TextInputWithLabel}
                  disabled={hasView}
                />
                <FieldDTO
                  control={control}
                  type="text"
                  className="mb-2"
                  dtoObject={attributes.comment}
                  addPlaceholder
                  component={TextInputWithLabel}
                  textarea
                  disabled={hasView}
                />

                <FieldDTO
                  control={control}
                  className="mb-2"
                  dtoObject={attributes.is_lpn_exact}
                  withLabel
                  component={Switches}
                  disabled={hasView}
                />
                <FieldDTO
                  control={control}
                  className="mb-2"
                  dtoObject={attributes.is_active}
                  withLabel
                  component={Switches}
                  disabled={hasView}
                />
                <FieldDTO
                  control={control}
                  className="mb-2"
                  dtoObject={attributes.is_vip}
                  withLabel
                  component={Switches}
                  disabled={hasView}
                />
                {!hasView ? (
                  <div className="text-center justify-content-center p-3">
                    <Button className="me-3 px-3" onClick={handleSave}>Сохранить</Button>
                    <Button variant="secondary" className="px-3" onClick={handleClose}>Закрыть</Button>
                  </div>
                ) : null}
              </Col>
              <Col columns={6} className="right-column">
                {currentSubscription?.id ? <IntervalGrid hasView={hasView} /> : renderCreateIntervalForm()}
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </div>
  );
}
